import { translate } from '../../i18n/index.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CreateMarketingCampaignInput,
  MarketingAudienceFilter,
  MarketingCampaign,
  MarketingCampaignReport,
  MarketingCampaignStepInput,
  MarketingDeliveryRow,
  MembershipStatus,
  MembershipTier,
} from '@atomfit/shared';
import { adminApi, type FranchiseTenant } from '../../api/admin.api.js';
import { gymsApi } from '../../api/gyms.api.js';
import type { Gym } from '../../types/index.js';
import { useAuth } from '../../hooks/useAuth.js';
import { CustomSelect, Spinner, Tag } from '../../components/design/AtomFitUI.js';

type Draft = CreateMarketingCampaignInput & { id?: string; status?: MarketingCampaign['status'] };

const emptyStep = (position: number): MarketingCampaignStepInput => ({
  position,
  delayMinutes: position === 0 ? 0 : 1440,
  subject:
    position === 0
      ? translate('pages.admin.admin_marketing_campaigns.empty_step.subject')
      : translate('pages.admin.admin_marketing_campaigns.empty_step.subject_2', {
          position: position + 1,
        }),
  htmlBody: translate('pages.admin.admin_marketing_campaigns.empty_step.html_body'),
  textBody: translate('pages.admin.admin_marketing_campaigns.empty_step.text_body'),
});

const emptyDraft = (type: 'BROADCAST' | 'SEQUENCE' = 'BROADCAST'): Draft => ({
  name:
    type === 'BROADCAST'
      ? translate('pages.admin.admin_marketing_campaigns.empty_draft.name')
      : translate('pages.admin.admin_marketing_campaigns.empty_draft.name_2'),
  type,
  audienceFilter: {},
  steps: type === 'BROADCAST' ? [emptyStep(0)] : [emptyStep(0), emptyStep(1)],
});

function apiError(error: unknown) {
  return (
    (error as { response?: { data?: { error?: { message?: string }; message?: string } } })
      ?.response?.data?.error?.message ??
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (error instanceof Error
      ? error.message
      : translate('pages.admin.admin_marketing_campaigns.api_error.error'))
  );
}

function inputDate(value?: string) {
  return value ? new Date(value).toISOString().slice(0, 10) : '';
}

function isoDate(value: string, end = false) {
  if (!value) return undefined;
  return pragueDateTimeToIso(`${value}T${end ? '23:59:59.999' : '00:00:00.000'}`);
}

function pragueDateTimeToIso(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{3}))?)?$/.exec(value);
  if (!match)
    throw new Error(
      translate('pages.admin.admin_marketing_campaigns.prague_date_time_to_iso.date'),
    );
  const [, year, month, day, hour, minute, second = '0', millisecond = '0'] = match;
  const wallClock = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(millisecond),
  );
  let result = new Date(wallClock);
  for (let iteration = 0; iteration < 2; iteration += 1) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Prague',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(result);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((item) => item.type === type)?.value ?? 0);
    const represented = Date.UTC(
      part('year'),
      part('month') - 1,
      part('day'),
      part('hour'),
      part('minute'),
      part('second'),
    );
    result = new Date(wallClock - (represented - result.getTime()));
  }
  return result.toISOString();
}

function campaignToDraft(campaign: MarketingCampaign): Draft {
  return {
    id: campaign.id,
    status: campaign.status,
    name: campaign.name,
    type: campaign.type,
    audienceFilter: campaign.audienceFilter ?? {},
    steps: campaign.steps.map(({ position, delayMinutes, subject, htmlBody, textBody }) => ({
      position,
      delayMinutes,
      subject,
      htmlBody,
      textBody,
    })),
  };
}

export function AdminMarketingCampaigns() {
  const { role } = useAuth();
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [tenants, setTenants] = useState<FranchiseTenant[]>([]);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [report, setReport] = useState<MarketingCampaignReport | null>(null);
  const [deliveries, setDeliveries] = useState<MarketingDeliveryRow[]>([]);
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function loadCampaigns(selectId?: string) {
    const response = await adminApi.listEmailCampaigns();
    setCampaigns(response.data.data);
    if (selectId) {
      const selected = response.data.data.find((item) => item.id === selectId);
      if (selected) setDraft(campaignToDraft(selected));
    }
  }

  useEffect(() => {
    async function loadInitialData() {
      const [campaignRes, tenantRes, firstGymRes] = await Promise.all([
        adminApi.listEmailCampaigns(),
        adminApi.listTenants(1, 100),
        gymsApi.adminList(role ?? '', 1),
      ]);
      const remainingGymPages = Array.from(
        { length: Math.max(0, firstGymRes.data.meta.totalPages - 1) },
        (_, index) => index + 2,
      );
      const remainingGyms = await Promise.all(
        remainingGymPages.map((page) => gymsApi.adminList(role ?? '', page)),
      );
      return {
        campaignRes,
        tenantRes,
        gyms: [
          firstGymRes.data.data,
          ...remainingGyms.map((response) => response.data.data),
        ].flat(),
      };
    }
    void loadInitialData()
      .then(({ campaignRes, tenantRes, gyms: loadedGyms }) => {
        setCampaigns(campaignRes.data.data);
        setTenants(tenantRes.data.data);
        setGyms(loadedGyms);
      })
      .catch((error) => setMessage(apiError(error)));
  }, [role]);

  useEffect(() => {
    if (!draft) return;
    const timer = window.setTimeout(() => {
      void adminApi
        .previewEmailAudience(draft.audienceFilter)
        .then((response) => setAudienceCount(response.data.count))
        .catch(() => setAudienceCount(null));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [draft?.audienceFilter]);

  useEffect(() => {
    setReport(null);
    setDeliveries([]);
    if (!draft?.id || draft.status === 'DRAFT') return;
    void Promise.all([
      adminApi.getEmailCampaignReport(draft.id),
      adminApi.listEmailCampaignDeliveries(draft.id),
    ]).then(([reportRes, deliveryRes]) => {
      setReport(reportRes.data);
      setDeliveries(deliveryRes.data.data);
    });
  }, [draft?.id, draft?.status]);

  function patchFilter(patch: Partial<MarketingAudienceFilter>) {
    setDraft((current) =>
      current ? { ...current, audienceFilter: { ...current.audienceFilter, ...patch } } : current,
    );
  }

  function patchStep(index: number, patch: Partial<MarketingCampaignStepInput>) {
    setDraft((current) =>
      current
        ? {
            ...current,
            steps: current.steps.map((step, stepIndex) =>
              stepIndex === index ? { ...step, ...patch } : step,
            ),
          }
        : current,
    );
  }

  async function saveDraft() {
    if (!draft) return;
    setBusy(true);
    setMessage(null);
    try {
      const payload: CreateMarketingCampaignInput = {
        name: draft.name,
        type: draft.type,
        audienceFilter: draft.audienceFilter,
        steps: draft.steps,
      };
      const response = draft.id
        ? await adminApi.updateEmailCampaign(draft.id, payload)
        : await adminApi.createEmailCampaign(payload);
      setDraft(campaignToDraft(response.data));
      await loadCampaigns(response.data.id);
      setMessage(translate('pages.admin.admin_marketing_campaigns.save_draft.set_message.save'));
    } catch (error) {
      setMessage(apiError(error));
    } finally {
      setBusy(false);
    }
  }

  async function act(action: 'test' | 'schedule' | 'pause' | 'resume' | 'cancel' | 'duplicate') {
    if (!draft?.id) return;
    setBusy(true);
    setMessage(null);
    try {
      if (action === 'test') {
        const response = await adminApi.testEmailCampaign(draft.id);
        setMessage(
          translate('pages.admin.admin_marketing_campaigns.act.set_message.sent', {
            value1: response.data.sentTo,
          }),
        );
      } else if (action === 'schedule') {
        if (!scheduleAt)
          throw new Error(translate('pages.admin.admin_marketing_campaigns.act.select'));
        const response = await adminApi.scheduleEmailCampaign(
          draft.id,
          pragueDateTimeToIso(scheduleAt),
        );
        setDraft(campaignToDraft(response.data));
        setMessage(
          translate('pages.admin.admin_marketing_campaigns.act.set_message.campaign_create'),
        );
      } else if (action === 'duplicate') {
        const response = await adminApi.duplicateEmailCampaign(draft.id);
        setDraft(campaignToDraft(response.data));
        setMessage(translate('pages.admin.admin_marketing_campaigns.act.set_message.create_edit'));
      } else {
        const response =
          action === 'pause'
            ? await adminApi.pauseEmailCampaign(draft.id)
            : action === 'resume'
              ? await adminApi.resumeEmailCampaign(draft.id)
              : await adminApi.cancelEmailCampaign(draft.id);
        setDraft(campaignToDraft(response.data));
        setMessage(
          action === 'cancel'
            ? translate('pages.admin.admin_marketing_campaigns.act.set_message.campaign_cancel')
            : action === 'pause'
              ? translate('pages.admin.admin_marketing_campaigns.act.set_message.campaign')
              : translate('pages.admin.admin_marketing_campaigns.act.set_message.campaign_2'),
        );
      }
      await loadCampaigns(action === 'duplicate' ? undefined : draft.id);
    } catch (error) {
      setMessage(apiError(error));
    } finally {
      setBusy(false);
    }
  }

  const editable = !draft?.status || draft.status === 'DRAFT';

  return (
    <section className="af-card" style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          alignItems: 'start',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            {translate(
              'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.div.campaign',
            )}
          </div>
          <div style={{ color: 'var(--af-muted)', fontSize: 13, marginTop: 4 }}>
            {translate(
              'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.div.email_campaign_create',
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="af-outline-btn"
            onClick={() => {
              setDraft(emptyDraft('BROADCAST'));
              setMessage(null);
            }}
          >
            {translate(
              'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_outline_btn.label',
            )}
          </button>
          <button
            className="af-primary-btn"
            onClick={() => {
              setDraft(emptyDraft('SEQUENCE'));
              setMessage(null);
            }}
          >
            {translate(
              'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_primary_btn.label',
            )}
          </button>
        </div>
      </div>

      {message && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'var(--af-s2)',
            color: 'var(--af-text)',
            fontSize: 13,
          }}
        >
          {message}
        </div>
      )}

      <div
        className="af-marketing-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 0.32fr) minmax(0, 1fr)',
          gap: 18,
          marginTop: 20,
        }}
      >
        <aside style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
          {campaigns.length === 0 && (
            <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
              {translate(
                'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.div.campaign_create',
              )}
            </div>
          )}
          {campaigns.map((campaign) => (
            <button
              key={campaign.id}
              className={draft?.id === campaign.id ? 'af-primary-btn' : 'af-outline-btn'}
              style={{
                display: 'grid',
                justifyItems: 'start',
                gap: 5,
                textAlign: 'left',
                padding: 12,
              }}
              onClick={() => setDraft(campaignToDraft(campaign))}
            >
              <span>{campaign.name}</span>
              <span style={{ fontSize: 10, opacity: 0.75 }}>
                {statusLabel(campaign.status)} {'·'}{' '}
                {campaign.type === 'BROADCAST'
                  ? translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.span.copy',
                    )
                  : translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.span.copy_2',
                      { value1: campaign.steps.length },
                    )}
              </span>
            </button>
          ))}
        </aside>

        <div style={{ minWidth: 0 }}>
          {!draft ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--af-muted)' }}>
              {translate(
                'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.div.campaign_create_select',
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 18 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr minmax(180px, 240px)',
                  gap: 12,
                }}
              >
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.label_label.copy',
                  )}
                  <input
                    className="af-input"
                    value={draft.name}
                    disabled={!editable}
                    onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  />
                </label>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.label_label.type',
                  )}
                  <CustomSelect
                    value={draft.type}
                    disabled={!editable}
                    onChange={(value) =>
                      setDraft({
                        ...draft,
                        type: value as Draft['type'],
                        steps:
                          value === 'BROADCAST'
                            ? [draft.steps[0] ?? emptyStep(0)]
                            : draft.steps.length > 1
                              ? draft.steps
                              : [...draft.steps, emptyStep(1)],
                      })
                    }
                    options={[
                      {
                        value: 'BROADCAST',
                        label: translate(
                          'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.custom_select.options',
                        ),
                      },
                      {
                        value: 'SEQUENCE',
                        label: translate(
                          'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.custom_select.options_2',
                        ),
                      },
                    ]}
                  />
                </label>
              </div>

              <AudienceBuilder
                filter={draft.audienceFilter}
                tenants={tenants}
                gyms={gyms}
                count={audienceCount}
                disabled={!editable}
                onChange={patchFilter}
              />

              <div style={{ display: 'grid', gap: 14 }}>
                {draft.steps.map((step, index) => (
                  <StepEditor
                    key={`${draft.id ?? 'new'}-${index}`}
                    step={step}
                    index={index}
                    disabled={!editable}
                    onChange={(patch) => patchStep(index, patch)}
                    {...(draft.type === 'SEQUENCE' && draft.steps.length > 1
                      ? {
                          onRemove: () =>
                            setDraft({
                              ...draft,
                              steps: draft.steps
                                .filter((_, itemIndex) => itemIndex !== index)
                                .map((item, position) => ({
                                  ...item,
                                  position,
                                  delayMinutes: position === 0 ? 0 : item.delayMinutes,
                                })),
                            }),
                        }
                      : {})}
                  />
                ))}
                {editable && draft.type === 'SEQUENCE' && (
                  <button
                    className="af-outline-btn"
                    style={{ justifySelf: 'start' }}
                    onClick={() =>
                      setDraft({ ...draft, steps: [...draft.steps, emptyStep(draft.steps.length)] })
                    }
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_outline_btn.email_add_next',
                    )}
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
                {editable && (
                  <button
                    className="af-primary-btn"
                    disabled={busy}
                    onClick={() => void saveDraft()}
                  >
                    {busy ? (
                      <Spinner />
                    ) : (
                      translate(
                        'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_primary_btn.save',
                      )
                    )}
                  </button>
                )}
                {editable && draft.id && (
                  <button
                    className="af-outline-btn"
                    disabled={busy}
                    onClick={() => void act('test')}
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_outline_btn.label_2',
                    )}
                  </button>
                )}
                {editable && draft.id && (
                  <label className="af-label" style={{ minWidth: 230 }}>
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.label_label.copy_2',
                    )}
                    <input
                      className="af-input"
                      type="datetime-local"
                      value={scheduleAt}
                      min={new Date().toISOString().slice(0, 16)}
                      onChange={(event) => setScheduleAt(event.target.value)}
                    />
                  </label>
                )}
                {editable && draft.id && (
                  <button
                    className="af-primary-btn"
                    disabled={busy || audienceCount === 0}
                    onClick={() => void act('schedule')}
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_primary_btn.label_2',
                    )}
                  </button>
                )}
                {draft.id && !editable && (
                  <button
                    className="af-outline-btn"
                    disabled={busy}
                    onClick={() => void act('duplicate')}
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_outline_btn.label_3',
                    )}
                  </button>
                )}
                {draft.status && ['SCHEDULED', 'RUNNING'].includes(draft.status) && (
                  <button
                    className="af-outline-btn"
                    disabled={busy}
                    onClick={() => void act('pause')}
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_outline_btn.status',
                    )}
                  </button>
                )}
                {draft.status === 'PAUSED' && (
                  <button
                    className="af-primary-btn"
                    disabled={busy}
                    onClick={() => void act('resume')}
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_primary_btn.label_3',
                    )}
                  </button>
                )}
                {draft.status && !['COMPLETED', 'CANCELLED', 'DRAFT'].includes(draft.status) && (
                  <button
                    className="af-outline-btn"
                    disabled={busy}
                    onClick={() => void act('cancel')}
                  >
                    {translate(
                      'pages.admin.admin_marketing_campaigns.admin_marketing_campaigns.button_outline_btn.cancel',
                    )}
                  </button>
                )}
                {draft.status && <Tag>{statusLabel(draft.status)}</Tag>}
              </div>

              {report && <CampaignReport report={report} deliveries={deliveries} />}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function AudienceBuilder({
  filter,
  tenants,
  gyms,
  count,
  disabled,
  onChange,
}: {
  filter: MarketingAudienceFilter;
  tenants: FranchiseTenant[];
  gyms: Gym[];
  count: number | null;
  disabled: boolean;
  onChange: (patch: Partial<MarketingAudienceFilter>) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--af-border)',
        borderRadius: 8,
        padding: 16,
        background: 'var(--af-s1)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 800 }}>
            {translate('pages.admin.admin_marketing_campaigns.audience_builder.div.audience')}
          </div>
          <div style={{ color: 'var(--af-muted)', fontSize: 12 }}>
            {translate('pages.admin.admin_marketing_campaigns.audience_builder.div.terms')}
          </div>
        </div>
        <Tag>
          {count === null
            ? translate('pages.admin.admin_marketing_campaigns.audience_builder.tag.copy')
            : translate('pages.admin.admin_marketing_campaigns.audience_builder.tag.recipient', {
                count: count,
              })}
        </Tag>
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: 12 }}
      >
        <DateRange
          label={translate(
            'pages.admin.admin_marketing_campaigns.audience_builder.date_range.registration',
          )}
          from={inputDate(filter.registeredFrom)}
          to={inputDate(filter.registeredTo)}
          disabled={disabled}
          onChange={(from, to) =>
            onChange({ registeredFrom: isoDate(from), registeredTo: isoDate(to, true) })
          }
        />

        <DateRange
          label={translate(
            'pages.admin.admin_marketing_campaigns.audience_builder.date_range.booking',
          )}
          from={inputDate(filter.lastBookingFrom)}
          to={inputDate(filter.lastBookingTo)}
          disabled={disabled}
          onChange={(from, to) =>
            onChange({ lastBookingFrom: isoDate(from), lastBookingTo: isoDate(to, true) })
          }
        />

        <MultiSelect
          label={translate(
            'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.tenant',
          )}
          values={filter.tenantIds ?? []}
          disabled={disabled}
          options={tenants.map((item) => ({ value: item.id, label: item.name }))}
          onChange={(tenantIds) => onChange({ tenantIds })}
        />

        <MultiSelect
          label={translate(
            'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.gym',
          )}
          values={filter.gymIds ?? []}
          disabled={disabled}
          options={gyms.map((item) => ({ value: item.id, label: item.name }))}
          onChange={(gymIds) => onChange({ gymIds })}
        />

        <MultiSelect
          label={translate(
            'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.membership',
          )}
          values={filter.membershipTiers ?? []}
          disabled={disabled}
          options={[
            translate(
              'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.options',
            ),
            translate(
              'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.options_2',
            ),
            translate(
              'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.options_3',
            ),
          ].map((value) => ({
            value,
            label: value,
          }))}
          onChange={(values) => onChange({ membershipTiers: values as MembershipTier[] })}
        />

        <MultiSelect
          label={translate(
            'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.membership_2',
          )}
          values={filter.membershipStatuses ?? []}
          disabled={disabled}
          options={[
            translate(
              'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.options_4',
            ),
            translate(
              'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.options_5',
            ),
            translate(
              'pages.admin.admin_marketing_campaigns.audience_builder.multi_select.options_6',
            ),
          ].map((value) => ({ value, label: value }))}
          onChange={(values) => onChange({ membershipStatuses: values as MembershipStatus[] })}
        />
      </div>
    </div>
  );
}

function DateRange({
  label,
  from,
  to,
  disabled,
  onChange,
}: {
  label: string;
  from: string;
  to: string;
  disabled: boolean;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="af-label">{label}</legend>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <input
          className="af-input"
          type="date"
          value={from}
          onChange={(event) => onChange(event.target.value, to)}
        />

        <input
          className="af-input"
          type="date"
          value={to}
          onChange={(event) => onChange(from, event.target.value)}
        />
      </div>
    </fieldset>
  );
}

function MultiSelect({
  label,
  values,
  options,
  disabled,
  onChange,
}: {
  label: string;
  values: string[];
  options: Array<{ value: string; label: string }>;
  disabled: boolean;
  onChange: (values: string[]) => void;
}) {
  return (
    <label className="af-label">
      {label}
      <select
        className="af-input"
        multiple
        disabled={disabled}
        value={values}
        onChange={(event) =>
          onChange(Array.from(event.target.selectedOptions, (option) => option.value))
        }
        style={{ minHeight: 88 }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StepEditor({
  step,
  index,
  disabled,
  onChange,
  onRemove,
}: {
  step: MarketingCampaignStepInput;
  index: number;
  disabled: boolean;
  onChange: (patch: Partial<MarketingCampaignStepInput>) => void;
  onRemove?: () => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [source, setSource] = useState(false);
  useEffect(() => {
    if (editorRef.current && !source) editorRef.current.innerHTML = step.htmlBody;
  }, [step.position, source]);
  const preview = useMemo(
    () =>
      step.htmlBody
        .replace(/\{\{\s*firstName\s*\}\}/g, 'Anna')
        .replace(/\{\{\s*lastName\s*\}\}/g, 'Nováková')
        .replace(/\{\{\s*email\s*\}\}/g, 'anna@example.com')
        .replace(/\{\{\s*unsubscribeUrl\s*\}\}/g, '#'),
    [step.htmlBody],
  );
  function command(name: string, value?: string) {
    editorRef.current?.focus();
    document.execCommand(name, false, value);
    onChange({ htmlBody: editorRef.current?.innerHTML ?? step.htmlBody });
  }
  return (
    <div style={{ border: '1px solid var(--af-border)', borderRadius: 8, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 14px',
          background: 'var(--af-s2)',
          borderBottom: '1px solid var(--af-border)',
        }}
      >
        <strong>
          {translate('pages.admin.admin_marketing_campaigns.step_editor.strong.email')} {index + 1}
        </strong>
        {onRemove && !disabled && (
          <button className="af-outline-btn" onClick={onRemove}>
            {translate(
              'pages.admin.admin_marketing_campaigns.step_editor.button_outline_btn.label',
            )}
          </button>
        )}
      </div>
      <div style={{ padding: 16, display: 'grid', gap: 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: index === 0 ? '1fr' : 'minmax(140px, 0.25fr) 1fr',
            gap: 12,
          }}
        >
          {index > 0 && (
            <label className="af-label">
              {translate('pages.admin.admin_marketing_campaigns.step_editor.label_label.previous')}
              <input
                className="af-input"
                type="number"
                min={1}
                disabled={disabled}
                value={Math.round(step.delayMinutes / 60)}
                onChange={(event) =>
                  onChange({ delayMinutes: Math.max(1, Number(event.target.value)) * 60 })
                }
              />
            </label>
          )}
          <label className="af-label">
            {translate('pages.admin.admin_marketing_campaigns.step_editor.label_label.subject')}
            <input
              className="af-input"
              disabled={disabled}
              value={step.subject}
              onChange={(event) => onChange({ subject: event.target.value })}
            />
          </label>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 0.75fr)',
            gap: 14,
          }}
        >
          <div>
            {!disabled && (
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  padding: 8,
                  background: 'var(--af-s2)',
                  border: '1px solid var(--af-border)',
                  borderBottom: 0,
                }}
              >
                <button className="af-outline-btn" onClick={() => command('bold')}>
                  {translate(
                    'pages.admin.admin_marketing_campaigns.step_editor.button_outline_btn.label_2',
                  )}
                </button>
                <button className="af-outline-btn" onClick={() => command('italic')}>
                  {translate(
                    'pages.admin.admin_marketing_campaigns.step_editor.button_outline_btn.label_3',
                  )}
                </button>
                <button className="af-outline-btn" onClick={() => command('formatBlock', 'h2')}>
                  {translate(
                    'pages.admin.admin_marketing_campaigns.step_editor.button_outline_btn.label_4',
                  )}
                </button>
                <button className="af-outline-btn" onClick={() => setSource(!source)}>
                  {translate(
                    'pages.admin.admin_marketing_campaigns.step_editor.button_outline_btn.label_5',
                  )}
                </button>
              </div>
            )}
            {source ? (
              <textarea
                className="af-input af-mono"
                rows={12}
                disabled={disabled}
                value={step.htmlBody}
                onChange={(event) => onChange({ htmlBody: event.target.value })}
              />
            ) : (
              <div
                ref={editorRef}
                contentEditable={!disabled}
                suppressContentEditableWarning
                onInput={() =>
                  onChange({ htmlBody: editorRef.current?.innerHTML ?? step.htmlBody })
                }
                style={{
                  minHeight: 260,
                  border: '1px solid var(--af-border)',
                  padding: 18,
                  outline: 'none',
                  background: '#fff',
                  color: '#0D0D0D',
                }}
              />
            )}
          </div>
          <iframe
            title={translate('pages.admin.admin_marketing_campaigns.step_editor.iframe.email', {
              value1: index + 1,
            })}
            sandbox=""
            srcDoc={`<!doctype html><body style="font-family:Arial;padding:22px;line-height:1.6;color:#111">${preview}<hr><p style="font-size:11px;color:#777">${translate('pages.admin.admin_marketing_campaigns.step_editor.iframe.src_doc')} · <u>${translate('pages.admin.admin_marketing_campaigns.step_editor.iframe.src_doc_2')}</u></p></body>`}
            style={{
              width: '100%',
              height: 330,
              border: '1px solid var(--af-border)',
              borderRadius: 8,
              background: '#fff',
            }}
          />
        </div>
        <label className="af-label">
          {translate('pages.admin.admin_marketing_campaigns.step_editor.label_label.copy')}
          <textarea
            className="af-input"
            rows={4}
            disabled={disabled}
            value={step.textBody}
            onChange={(event) => onChange({ textBody: event.target.value })}
          />
        </label>
        <div style={{ color: 'var(--af-muted)', fontSize: 11 }}>
          {translate('pages.admin.admin_marketing_campaigns.step_editor.div.copy')}{' '}
          {translate('pages.admin.admin_marketing_campaigns.step_editor.div.name')}
          {','} {translate('pages.admin.admin_marketing_campaigns.step_editor.div.name_2')}
          {','} {translate('pages.admin.admin_marketing_campaigns.step_editor.div.email')}
          {','} {translate('pages.admin.admin_marketing_campaigns.step_editor.div.copy_2')}
        </div>
      </div>
    </div>
  );
}

function CampaignReport({
  report,
  deliveries,
}: {
  report: MarketingCampaignReport;
  deliveries: MarketingDeliveryRow[];
}) {
  const stats: Array<[string, number]> = [
    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats'), report.enrolled],
    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_2'), report.pending],
    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_3'), report.sent],
    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_4'), report.delivered],
    [
      translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_5'),
      report.uniqueOpened,
    ],

    [
      translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_6'),
      report.uniqueClicked,
    ],

    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_7'), report.bounced],
    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_8'), report.complained],
    [translate('pages.admin.admin_marketing_campaigns.campaign_report.stats_9'), report.failed],
  ];

  return (
    <div style={{ borderTop: '1px solid var(--af-border)', paddingTop: 18 }}>
      <div style={{ fontWeight: 900, marginBottom: 12 }}>
        {translate('pages.admin.admin_marketing_campaigns.campaign_report.div.copy')}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(90px,1fr))',
          gap: 8,
        }}
      >
        {stats.map(([label, value]) => (
          <div key={label} style={{ padding: 12, background: 'var(--af-s2)', borderRadius: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 900 }}>{value}</div>
            <div style={{ color: 'var(--af-muted)', fontSize: 10 }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ color: 'var(--af-muted)', fontSize: 11, marginTop: 8 }}>
        {translate('pages.admin.admin_marketing_campaigns.campaign_report.div.email_privacy_open')}{' '}
        {report.totalOpens}
        {translate('pages.admin.admin_marketing_campaigns.campaign_report.div.clicks')}{' '}
        {report.totalClicks}
        {'.'}
      </div>
      {deliveries.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <table className="af-table">
            <thead>
              <tr>
                <th>
                  {translate('pages.admin.admin_marketing_campaigns.campaign_report.th.recipient')}
                </th>
                <th>
                  {translate('pages.admin.admin_marketing_campaigns.campaign_report.th.copy')}
                </th>
                <th>
                  {translate('pages.admin.admin_marketing_campaigns.campaign_report.th.status')}
                </th>
                <th>
                  {translate('pages.admin.admin_marketing_campaigns.campaign_report.th.open')}
                </th>
                <th>
                  {translate('pages.admin.admin_marketing_campaigns.campaign_report.th.clicks')}
                </th>
                <th>
                  {translate('pages.admin.admin_marketing_campaigns.campaign_report.th.error')}
                </th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((row) => (
                <tr key={row.id}>
                  <td>{row.recipient}</td>
                  <td>{row.stepPosition + 1}</td>
                  <td>{row.status}</td>
                  <td>{row.openCount}</td>
                  <td>{row.clickCount}</td>
                  <td>{row.error ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function statusLabel(status: MarketingCampaign['status']) {
  return (
    {
      DRAFT: translate('pages.admin.admin_marketing_campaigns.status_label.draft'),
      SCHEDULED: translate('pages.admin.admin_marketing_campaigns.status_label.scheduled'),
      RUNNING: translate('pages.admin.admin_marketing_campaigns.status_label.running'),
      PAUSED: translate('pages.admin.admin_marketing_campaigns.status_label.paused'),
      COMPLETED: translate('pages.admin.admin_marketing_campaigns.status_label.completed'),
      CANCELLED: translate('pages.admin.admin_marketing_campaigns.status_label.cancelled'),
    } as const
  )[status];
}
